import Checkbox from 'components/Form/Checkbox';
import InputHidden from 'components/Form/InputHidden';
import SubmitButton from 'components/Form/SubmitButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import {TeamMemberPermissions} from 'components/Navigation/NavigationTabs/Team/Member';
import SectionHeader from 'components/SectionHeader';
import TableStatic from 'components/Table/TableStatic';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';
import {Backend} from 'types/api';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';

import useEditTeamNotificationEmployee from './services';
import {notificationTable} from './styles.module.css';
import {TeamNotificationEmployeeEditFormData} from './types';

export type TeamNotificationEmployeeEditBackendData = Backend<{
    formsetData: Array<{
        displayName: string;
        fields: Array<{name: string; value: boolean | string}>;
        name: string;
    }>;
    isTeamContext: boolean;
    member: {email: string; mid: string; name: string};
    myActivityDisabledSettingsList: string[];
    navigationTabs: TeamMemberPermissions;
    partnerName: string;
    teamActivityDisabledSettingsList: string[];
}>;

const TeamNotificationEmployeeEdit = () => {
    const {
        data: {
            formsetData,
            isTeamContext,
            member: {email, mid, name},
            myActivityDisabledSettingsList,
            navigationTabs,
            partnerName,
            teamActivityDisabledSettingsList,
        },
    } = useBackend<TeamNotificationEmployeeEditBackendData>();
    const methods = useForm<TeamNotificationEmployeeEditFormData>();

    const {mutateAsync} = useEditTeamNotificationEmployee();

    const {handleSubmit} = methods;

    const myActivityDisabledSettingsSet = new Set(
        myActivityDisabledSettingsList,
    );

    const teamActivityDisabledSettingsSet = new Set(
        teamActivityDisabledSettingsList,
    );

    const isCheckboxShown = (formName: string, fieldName: string) => {
        if (fieldName.endsWith('my_activity_enabled')) {
            return myActivityDisabledSettingsSet.has(formName);
        } else if (fieldName.endsWith('team_activity_enabled')) {
            return teamActivityDisabledSettingsSet.has(formName);
        }

        // Invalid pattern
        return false;
    };

    const onSubmit = async (formData: TeamNotificationEmployeeEditFormData) => {
        mutateAsync({formData, mid})
            .then(
                onApiSuccess({
                    onRedirect: (response) => {
                        if (
                            response.name ===
                            'team_notification_employee_mid_react_view'
                        ) {
                            return setUrl(
                                window.Urls.team_notification_employee_mid_react_view(
                                    mid,
                                ),
                            );
                        }

                        if (response.name === 'notifications_view') {
                            return setUrl(window.Urls.notifications_view());
                        }

                        throw new Error('Unhandled redirect');
                    },
                }),
            )
            .catch(onApiError(methods));
    };

    return (
        <>
            <SectionHeader title={name} />
            <NavigationTabs.TeamMember
                className="my-4"
                mid={mid}
                selection="notifications"
                {...navigationTabs}
            />
            <FormProvider {...methods}>
                <form className="mb-8" onSubmit={handleSubmit(onSubmit)}>
                    {isTeamContext && (
                        <InputHidden name="team_context" value="True" />
                    )}
                    <TableStatic
                        className={twMerge(
                            notificationTable,
                            'mt-6 w-full min-w-[720px]',
                        )}
                        hasStripes={true}
                        id="member-table"
                    >
                        <TableStatic.Header>
                            <TableStatic.HeaderRow>
                                <TableStatic.Column colSpan={2}>
                                    {partnerName} - {email}
                                </TableStatic.Column>
                                <TableStatic.Column />
                                <TableStatic.Column />
                            </TableStatic.HeaderRow>
                        </TableStatic.Header>
                        <TableStatic.Body>
                            <TableStatic.Row>
                                <TableStatic.Cell />
                                <TableStatic.Cell className="whitespace-nowrap text-center">
                                    {t`I../My..`}
                                </TableStatic.Cell>
                                <TableStatic.Cell className="whitespace-nowrap text-center">
                                    {t`My team../My Team's..`}
                                </TableStatic.Cell>
                            </TableStatic.Row>
                            {formsetData.map((form) => (
                                <TableStatic.Row key={form.name}>
                                    <TableStatic.Cell>
                                        {form.displayName}
                                    </TableStatic.Cell>
                                    {form.fields.map((field) => (
                                        <TableStatic.Cell key={field.name}>
                                            {field.name.endsWith('id') ? (
                                                <InputHidden
                                                    name={field.name}
                                                    value={
                                                        field.value as string
                                                    }
                                                />
                                            ) : isCheckboxShown(
                                                  form.name,
                                                  field.name,
                                              ) ? null : (
                                                <>
                                                    <Checkbox
                                                        defaultChecked={
                                                            field.value as boolean
                                                        }
                                                        name={field.name}
                                                    />
                                                </>
                                            )}
                                        </TableStatic.Cell>
                                    ))}
                                </TableStatic.Row>
                            ))}
                        </TableStatic.Body>
                    </TableStatic>
                    <SubmitButton className="mt-6">{t`Save`}</SubmitButton>
                </form>
            </FormProvider>
        </>
    );
};

export default TeamNotificationEmployeeEdit;
